export type PolicySection = {
  id: string
  title: string
  summary?: string
  paragraphs?: string[]
  bullets?: string[]
}

export const membershipPolicySections: PolicySection[] = [
  {
    id: 'local-members',
    title: 'Local membership area',
    paragraphs: [
      'For membership-policy purposes, local members are those who live in the Las Vegas metropolitan area, North Las Vegas, Henderson, or Boulder City.',
    ],
  },
  {
    id: 'approval-only',
    title: 'Approval-only membership paths',
    paragraphs: [
      'Wayfarers and Revelers are approval-only paths. They are shown for information and cannot be selected in the membership application. Applicants may discuss their circumstances with the Membership Committee; placement requires Guild Leader approval.',
      'The Events Committee may evaluate a Reveler proposal and make a recommendation. Final approval, continuation, or removal from either approval-only path rests with the Guild Leader.',
    ],
  },
  {
    id: 'patron-credits',
    title: 'Flamekeeper fee credits',
    paragraphs: [
      'Each Flamekeeper membership includes two fee credits per membership year. One credit covers one eligible guild or event fee, such as a site fee or a Pirate Fest portable-restroom fee.',
    ],
    bullets: [
      'Credits may be used in any combination for eligible fees.',
      'Credits are nontransferable, have no cash value, and expire at the end of the membership year.',
      'A member must claim a credit before the guild’s payment deadline for that fee.',
    ],
  },
  {
    id: 'exceptions',
    title: 'Private exceptions and accommodations',
    paragraphs: [
      'The Guild recognizes that health, disability, work, school, family, transportation, and other circumstances may affect participation. A member may privately request an exception, accommodation, alternative duty, or grace period.',
      'Each request is decided case by case by the Guild Leader or an authorized designee. An approval applies only to the member and circumstances reviewed, does not create a precedent, may include conditions or an end date, and may be changed or withdrawn if circumstances change.',
    ],
  },
  {
    id: 'renewal',
    title: 'Annual dues and renewal',
    paragraphs: [
      'Youth dues apply only to members under 18. Members age 18 and older pay adult dues. Members age 18 through 20 still follow the separate Under-21 Participation Agreement and camping safeguards.',
      'The membership year runs from February 20 through February 19 of the following year. Annual membership dues are due February 20. Before renewal, the guild will review the member’s verified participation from the previous membership year.',
      'If the member did not complete the requirements of the selected path, after approved exceptions and makeups are considered, the member must move to the path that matches the previous year’s actual participation. The member may pay the dues for that path or choose not to renew. If neither occurs, membership will end. Any grace period requires case-by-case approval from the Guild Leader.',
    ],
  },
  {
    id: 'makeups',
    title: 'Making up missed participation',
    paragraphs: [
      'Members are encouraged to propose creative ways to make up missed participation. A proposed makeup must add significant value to the guild experience, the guild’s readiness, or the experience Ourrenland provides to faire patrons.',
      'Makeups are not automatic. The responsible committee and the Guild Leader will review each proposal case by case, set the required scope and deadline, and confirm when the work is complete.',
    ],
  },
  {
    id: 'day-tripping',
    title: 'Day-tripping at faire',
    paragraphs: [
      'A member who attends a faire without camping is considered a day-tripper. Day-tripping changes overnight access; it does not remove the member’s tier responsibilities.',
    ],
    bullets: [
      'The member receives a removable wristband that must be returned at the end of each day.',
      'The member completes guild duty, setup, and teardown according to the member’s tier.',
      'The member must attend the guild potluck on the night before the first official event day.',
      'The member may remain up to one hour after the faire closes each day. On the final day, the member may remain until teardown is complete.',
    ],
  },
  {
    id: 'under-21',
    title: 'Members under 21',
    paragraphs: [
      'These safeguards reflect the amount and ready availability of alcohol at some events. They apply in addition to venue rules and applicable law.',
    ],
    bullets: [
      'A member under 18 must have a parent or legal guardian who is also a guild member and is present during events. The parent or guardian signs the Under-21 Participation Agreement.',
      'A member age 18 through 20 signs the Under-21 Participation Agreement for themselves.',
      'A member age 18 through 20 may not camp unless sharing the sleeping space with a parent or a partner who is at least 21 and is also a guild member.',
      'For this policy, a partner means a person in an established, deep, and meaningful relationship with the member—not merely a friend—and who shares the same sleeping space during the event.',
      'The over-21 parent or partner who sponsors camping must also sign the agreement and accept the responsibilities described in it.',
    ],
  },
  {
    id: 'faires',
    title: 'Faire participation',
    paragraphs: [
      'Ourrenland currently attends Age of Chivalry (AOC), Pirate Fest, Two Rivers, and London Bridge. The guild may add, remove, or change designated events as its calendar and opportunities develop.',
      'Viking Fest LV is anticipated to begin in November 2027 and may be added after the guild confirms its participation.',
    ],
  },
]

export const trialPolicySections: PolicySection[] = [
  {
    id: 'trial',
    title: 'New-member faire trial',
    paragraphs: [
      'A prospective member must submit a membership application before taking part in a faire trial. The trial is one faire and is intended to let both the applicant and Ourrenland experience working together before a membership decision is made.',
    ],
    bullets: [
      'The applicant attends setup, every public day of the faire, and teardown.',
      'The applicant day-trips and follows the guild’s wristband-return rules.',
      'The applicant completes guild duty at the Guardians level for the entire trial.',
      'A Membership Committee member serves as the applicant’s shadow, helps with orientation, and conducts a first-faire walkthrough.',
      'After the faire, the applicant tells the Membership Committee whether they wish to continue the membership process.',
      'The Membership Committee and Guild Leader then decide whether the applicant is a good fit and whether to extend a membership invitation.',
    ],
  },
  {
    id: 'membership-committee',
    title: 'Membership Committee responsibilities',
    bullets: [
      'Recruit prospective members and answer initial questions.',
      'Manage applicant review, orientation, and required acknowledgements.',
      'Provide first-faire walkthroughs and administer the shadow program.',
      'Record trial completion and make a fit recommendation to the Guild Leader.',
    ],
  },
]

export const codeOfConductSections: PolicySection[] = [
  {
    id: 'conduct-purpose',
    title: '1. Purpose and reach',
    paragraphs: [
      'Ourrenland is built on trust, shared work, creativity, and hospitality. This Code applies at guild meetings, craft days, faires, camps, committee work, online guild spaces, travel connected to guild activity, and any time a person represents Ourrenland to the public.',
    ],
  },
  {
    id: 'respect',
    title: '2. Respect and belonging',
    bullets: [
      'Treat members, applicants, patrons, performers, vendors, event staff, and neighboring guilds with dignity.',
      'Harassment, discrimination, bullying, stalking, threats, retaliation, or deliberately humiliating conduct are not acceptable.',
      'Respect names, pronouns, identities, accessibility needs, and reasonable personal boundaries.',
      'Conflict may happen. Address it directly and calmly when safe, or ask a committee chair or the Guild Leader for help.',
    ],
  },
  {
    id: 'consent',
    title: '3. Consent and personal boundaries',
    bullets: [
      'Ask before touching another person, their clothing, costume, mobility aid, belongings, or sleeping space.',
      'Consent must be freely given, specific, and ongoing. Silence, prior consent, flirtation, costume, or intoxication is not consent.',
      'Stop immediately when someone says no, withdraws consent, or appears unable to participate safely.',
      'Sexual activity, sexual harassment, and sexualized conduct involving a minor are prohibited.',
    ],
  },
  {
    id: 'public-conduct',
    title: '4. Patrons and public representation',
    bullets: [
      'Create a welcoming, age-appropriate patron experience and follow the event’s rules while in public-facing areas.',
      'Do not argue with patrons, event staff, or other guilds on behalf of Ourrenland. Bring a problem to the responsible chair or Guild Leader.',
      'Do not claim to speak for the guild, make commitments for the guild, or publish private guild information unless authorized.',
    ],
  },
  {
    id: 'alcohol',
    title: '5. Alcohol, substances, and under-21 safety',
    bullets: [
      'Follow all laws, venue rules, and the guild’s Under-21 Participation Agreement.',
      'No one may provide alcohol or controlled substances to a person who may not legally possess or consume them.',
      'Members remain responsible for their conduct while drinking. Intoxication does not excuse unsafe, disruptive, aggressive, or nonconsensual behavior.',
      'Anyone responsible for a minor or sponsoring an 18-to-20-year-old camper must remain able to fulfill that responsibility.',
    ],
  },
  {
    id: 'safety',
    title: '6. Camp, fire, weapons, and physical safety',
    bullets: [
      'Follow event, fire-marshal, food-safety, weapon, vehicle, and campsite rules.',
      'Use tools, weapons, fire, cooking equipment, and guild structures only when trained or authorized to do so.',
      'Report hazards, injuries, missing persons, damaged structures, and unsafe conduct promptly.',
      'Never use a weapon, prop, vehicle, animal, or physical force to intimidate another person.',
    ],
  },
  {
    id: 'privacy-property',
    title: '7. Privacy, property, and guild records',
    bullets: [
      'Do not enter another person’s tent, sleeping space, container, or private area without permission except in a genuine emergency.',
      'Use guild and personal property carefully. Ask before borrowing, report damage, and return items promptly.',
      'Keep applicant information, member records, accommodations, complaints, and committee-restricted information confidential.',
      'Obtain permission before posting identifiable images or sensitive information when a reasonable expectation of privacy exists.',
    ],
  },
  {
    id: 'work',
    title: '8. Commitments and shared work',
    bullets: [
      'Give accurate availability and communicate promptly when circumstances change.',
      'Complete assigned duties safely and to the agreed standard, or ask for help or reassignment before the duty is abandoned.',
      'Do not falsify attendance, participation, payment, committee, safety, or incident records.',
      'Respect approved accommodations and do not pressure another member to disclose private reasons for them.',
    ],
  },
  {
    id: 'reporting',
    title: '9. Reporting concerns',
    paragraphs: [
      'A member may report a concern to the Guild Leader, the relevant committee chair, or another person designated by the Guild Leader. Reports will be shared only as needed to review the concern, protect people, and make a fair decision. Knowingly false reports and retaliation against a person who raises or participates in a good-faith concern are prohibited.',
      'An immediate safety threat should be reported to event security, emergency services, or law enforcement as appropriate before or alongside a guild report.',
    ],
  },
  {
    id: 'accountability',
    title: '10. Review and consequences',
    paragraphs: [
      'The guild may use coaching, a warning, changed duties, removal from an activity or camp, suspension, conditions on return, or termination of membership. The response will consider safety, severity, pattern, impact, available evidence, and the person’s willingness to repair harm. The Guild Leader has final authority over guild consequences and may take temporary protective action while a concern is reviewed.',
    ],
  },
  {
    id: 'acknowledgement',
    title: '11. Member acknowledgement',
    paragraphs: [
      'Members age 18 and older must sign this Code of Conduct personally, including by accepted digital signature, before membership becomes active and again whenever the guild requires acknowledgement of a material revision. For a member under 18, the verified parent or legal guardian who is also a guild member signs on the youth member’s behalf. The guardian signs using their own name and guild account; the record identifies both the youth member and the guardian’s role.',
      'By signing, a member confirms that they have read, understood, and agree to follow the Code and related guild policies. The guild may revise this Code; members will be notified when a new signature is required.',
    ],
  },
]
